import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useTheme } from './ThemeProvider';
import { useLang } from './LanguageProvider';
import translations from '../translations';
import { Sun, Moon, Package, Settings, Sparkles, Shield, HelpCircle, MessageSquare, Rocket, ExternalLink } from 'lucide-react';
import Bayrak from './Bayraklar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import EnterpriseDemoPopup from './EnterpriseDemoPopup';
import MenuToggleIcon from './MenuToggleIcon';

/* ============================================================================
   ÜST ŞERİT (Navbar) — premium sürüm
   ----------------------------------------------------------------------------
   İKİ REFERANS
   · Masaüstü:  21st.dev "tubelight-navbar" — hap biçimli yüzen menü, aktif
                sekmenin üstünde tüp ışık parıltısı.
   · Mobil:     21st.dev "menu-toggle-icon" — çizgileri dönerek çarpıya evrilen
                hamburger (bkz. MenuToggleIcon.js).

   framer-motion KULLANILMIYOR. Referanstaki `layoutId="lamp"` etkisi, tek bir
   mutlak konumlu lamba çiftinin `transform` ile taşınmasıyla üretiliyor.
   Geçiş eğrisinin sayısal gerekçesi App.css'te (.wh-lamba blokları).

   PERFORMANS KURALLARI (bu dosyada uyulanlar)
   1. Sayfada TEK bir `scroll` dinleyicisi var; `passive: true` ve
      requestAnimationFrame ile kısıtlı. Yalnızca `window.scrollY` okunuyor —
      hiçbir `getBoundingClientRect` çağrısı kaydırma sırasında çalışmıyor.
   2. Aktif bölüm tespiti kaydırma olayıyla DEĞİL, tek bir
      IntersectionObserver ile yapılıyor (tarayıcı bunu ana iş parçacığı
      dışında hesaplar).
   3. Lamba yalnızca `transform` + `opacity` ile hareket ediyor; ölçüm
      (offsetLeft/offsetWidth) sadece aktif sekme, dil veya boyut değişince
      yapılıyor — karede bir değil, olayda bir.
   4. TEK bir ResizeObserver var, her sekme için ayrı değil; şeridin kendisini
      izliyor ve rAF ile kısıtlı.
   5. `backdrop-filter: blur()` yalnızca tek katmanda (nav'ın kendisinde) ve
      yalnızca kaydırma sonrası uygulanıyor; hap şeridinde tekrarlanmıyor.

   ÇÖZÜLEN GERİLEME (ölçümle bulundu)
   Eski sürümde `.wh-cta` sınıfı doğrudan `hidden sm:inline-flex` ile aynı
   elemanda kullanılıyordu. App.css, Tailwind çıktısından SONRA yükleniyor ve
   `.wh-cta { display: inline-flex }` ile `.hidden { display: none }` aynı
   özgüllükte olduğu için `.hidden` eziliyordu. Sonuç: 320–414px'te iki CTA
   düğmesi görünmeye devam ediyor, hamburger x=441px'e itiliyor ve EKRAN
   DIŞINDA kalıyordu (Playwright ölçümü: clientW=320, hamburger sol kenarı
   441px). Artık CTA'lar görünürlüğü taşıyan sade bir sarmalayıcı `div`in
   içinde; `.wh-cta` sınıfları ve `clamp()` boyutlandırması aynen korundu.
   ========================================================================= */

/* Tek sayfa yerleşimindeki bölümlerin id'leri — DOM sırasıyla. */
const BOLUM_IDLERI = ['products', 'how-it-works', 'features', 'security', 'faq'];

export default function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const { lang, toggleLang } = useLang();
  const t = translations[lang].nav;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const scrollAtOpenRef = useRef(0);
  const hamburgerRef = useRef(null);
  const mobilPanelRef = useRef(null);
  const seritRef = useRef(null);
  const sekmeRef = useRef({});

  const navLinks = [
    { id: 'products', label: t.products, href: '#products', icon: Package },
    { id: 'how-it-works', label: t.howItWorks, href: '#how-it-works', icon: Settings },
    { id: 'features', label: t.features, href: '#features', icon: Sparkles },
    { id: 'security', label: t.security, href: '#security', icon: Shield },
    { id: 'faq', label: t.faq, href: '#faq', icon: HelpCircle },
    { id: 'contact', label: t.contact, href: '/contact', icon: MessageSquare },
  ];

  const closeMobileMenu = useCallback(() => setMobileOpen(false), []);

  /* ---------------------------------------------------------------------
     1) TEK KAYDIRMA DİNLEYİCİSİ
     Hem "kaydırıldı mı" durumunu hem de "mobil menüyü kapat" kuralını aynı
     dinleyici yürütüyor. İkisi ayrı olsaydı sayfada iki abone olurdu.
     rAF ile kısıtlı, passive, sadece scrollY okuyor.
     --------------------------------------------------------------------- */
  const mobilAcikRef = useRef(false);
  useEffect(() => { mobilAcikRef.current = mobileOpen; }, [mobileOpen]);

  useEffect(() => {
    let bekliyor = false;
    const kaydirma = () => {
      if (bekliyor) return;
      bekliyor = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(onceki => {
          const yeni = y > 20;
          return onceki === yeni ? onceki : yeni;
        });
        if (mobilAcikRef.current && Math.abs(y - scrollAtOpenRef.current) > 60) {
          setMobileOpen(false);
        }
        bekliyor = false;
      });
    };
    window.addEventListener('scroll', kaydirma, { passive: true });
    return () => window.removeEventListener('scroll', kaydirma);
  }, []);

  /* ---------------------------------------------------------------------
     2) GERÇEK AKTİF SEKME
     Referans kodda ilk sekme her zaman aktifti — burada değil.
     · Ayrı rotalarda (/contact) rota belirliyor.
     · /terms, /privacy gibi karşılığı olmayan rotalarda aktif sekme YOK.
     · Ana sayfada tek bir IntersectionObserver, görüş alanının %30–%40
       bandına giren bölümü aktif sayıyor. Sayfanın tepesindeyken (kahraman
       bölümü) hiçbir sekme aktif olmuyor — çünkü gerçekten değil.
     --------------------------------------------------------------------- */
  const [aktifId, setAktifId] = useState(null);
  const gorunurRef = useRef(new Set());

  useEffect(() => {
    if (location.pathname === '/contact') { setAktifId('contact'); return undefined; }
    if (!isHome) { setAktifId(null); return undefined; }

    gorunurRef.current = new Set();
    const hedefler = BOLUM_IDLERI
      .map(id => document.getElementById(id))
      .filter(Boolean);
    if (!hedefler.length) { setAktifId(null); return undefined; }

    const gozlemci = new IntersectionObserver(
      girisler => {
        for (const g of girisler) {
          if (g.isIntersecting) gorunurRef.current.add(g.target.id);
          else gorunurRef.current.delete(g.target.id);
        }
        const ilk = BOLUM_IDLERI.find(id => gorunurRef.current.has(id)) || null;
        setAktifId(onceki => (onceki === ilk ? onceki : ilk));
      },
      /* Bant = görüş alanının %30 ile %40'ı arası (yüksekliğin %10'u).
         Dar bant tek bir bölümün seçilmesini garantiliyor; bölümler
         py-20+ olduğu için hiçbiri bandın içine sığmayacak kadar kısa
         değil. threshold [0] — en ucuz eşik. */
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );
    hedefler.forEach(h => gozlemci.observe(h));
    return () => gozlemci.disconnect();
  }, [isHome, location.pathname]);

  /* ---------------------------------------------------------------------
     3) LAMBA KONUMU
     Aktif sekmenin şerit içindeki yerini ölçüp transform'a çeviriyoruz.
     offsetLeft/offsetWidth, şerit `position: relative` olduğu için doğrudan
     şeride göre geliyor (offsetParent = şerit).
     --------------------------------------------------------------------- */
  const [lamba, setLamba] = useState(null);      // { x, w }
  const [ilkKare, setIlkKare] = useState(true);  // ilk yerleşimde geçiş kapalı
  const aktifIdRef = useRef(null);

  /* Ölçüm işini yapan tek fonksiyon. Kimlik DIŞARIDAN veriliyor: ref'ten
     okusaydı, useLayoutEffect ref'i güncelleyen useEffect'ten ÖNCE çalıştığı
     için lamba her zaman bir adım geride kalırdı (ölçümle yakalandı:
     "Security"ye tıklandığında lamba "Products"ın yerine gidiyordu). */
  const olc = useCallback((id) => {
    const el = id ? sekmeRef.current[id] : null;
    const serit = seritRef.current;
    if (!el || !serit) return;
    const x = el.offsetLeft;
    const w = el.offsetWidth;
    /* Şeridin DOLGU kutusu yüksekliği. Lamba zemini `height:100%` ile bunu
       kaplıyor; hap yarıçapını doğru hesaplamak için gerekiyor (aşağıda). */
    const h = serit.clientHeight;
    setLamba(onceki =>
      (onceki && onceki.x === x && onceki.w === w && onceki.h === h ? onceki : { x, w, h }));
  }, []);

  /* Bu useLayoutEffect, aşağıdaki ResizeObserver'ın okuduğu ref'i de tazeliyor;
     bildirim sırası açısından ölçümden ÖNCE çalışması şart. */
  useLayoutEffect(() => {
    aktifIdRef.current = aktifId;
    olc(aktifId);
  }, [aktifId, lang, olc]);

  const lambayiOlc = useCallback(() => { olc(aktifIdRef.current); }, [olc]);

  /* İlk konum yerleştikten SONRA geçişi aç — böylece lamba açılışta
     ekranın solundan kayarak gelmiyor, olması gereken yerde beliriyor. */
  useEffect(() => {
    if (!lamba || !ilkKare) return undefined;
    const kimlik = requestAnimationFrame(() => setIlkKare(false));
    return () => cancelAnimationFrame(kimlik);
  }, [lamba, ilkKare]);

  /* TEK ResizeObserver — şeridi izliyor, rAF ile kısıtlı. */
  useEffect(() => {
    const serit = seritRef.current;
    if (!serit || typeof ResizeObserver === 'undefined') return undefined;
    let bekliyor = false;
    const gozlemci = new ResizeObserver(() => {
      if (bekliyor) return;
      bekliyor = true;
      requestAnimationFrame(() => { bekliyor = false; lambayiOlc(); });
    });
    gozlemci.observe(serit);
    return () => gozlemci.disconnect();
  }, [lambayiOlc]);

  /* ---------------------------------------------------------------------
     4) MOBİL MENÜ — erişilebilirlik
     Escape kapatıyor ve odağı hamburgere geri veriyor; menü açılınca odak
     ilk bağlantıya gidiyor.
     --------------------------------------------------------------------- */
  useEffect(() => {
    if (!mobileOpen) return undefined;
    scrollAtOpenRef.current = window.scrollY;
    const tus = e => {
      if (e.key !== 'Escape') return;
      setMobileOpen(false);
      if (hamburgerRef.current) hamburgerRef.current.focus();
    };
    document.addEventListener('keydown', tus);
    const kimlik = requestAnimationFrame(() => {
      const ilk = mobilPanelRef.current && mobilPanelRef.current.querySelector('a, button');
      if (ilk) ilk.focus();
    });
    return () => { document.removeEventListener('keydown', tus); cancelAnimationFrame(kimlik); };
  }, [mobileOpen]);

  /* Rota değişince menü kapansın (aksi halde /contact'a gidince açık kalır). */
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    /* Sayfa bağlantısı (/ ile başlıyor) — React Router, tam yeniden yükleme yok. */
    if (href.startsWith('/')) {
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!isHome) {
      navigate('/' + href);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  /* ---------------------------------------------------------------------
     LAMBA GEOMETRİSİ — köşe oturması
     Şeridin yatay/dikey dolgusu SIFIR (App.css `.wh-serit`). Sekmelerin kendi
     dolgusu boşluğu veriyor. Böylece ilk sekmenin `offsetLeft`i 0, son
     sekmenin sağ kenarı da şeridin iç kenarı oluyor; lamba zemini köşeye
     TAM oturuyor (kalan tek fark şeridin 1px'lik kendi çerçevesi).

     YARIÇAP DÜZELTMESİ: zemin 100px genişliğinde durup `scaleX(w/100)` ile
     esniyor. `border-radius: 9999px` yazsaydık ölçekleme yatay yarıçapı da
     çarpardı ve hap, şeridin köşesiyle aynı eğriyi tutturamazdı. Bu yüzden
     yatay yarıçap ölçeğin TERSİYLE çarpılıyor:
        yatay = (h/2) / sx        dikey = h/2
     Ölçeklendikten sonra yatay yarıçap tam olarak h/2 oluyor — yani şeridin
     iç köşe yarıçapının aynısı. (h = şeridin dolgu kutusu yüksekliği.) */
  const sx = lamba && lamba.w > 0 ? lamba.w / 100 : 0;
  const yariCap = lamba ? lamba.h / 2 : 0;
  const lambaStili = lamba && sx > 0
    ? {
        transform: `translate3d(${lamba.x}px,0,0) scaleX(${sx.toFixed(4)})`,
        borderRadius: `${(yariCap / sx).toFixed(2)}px / ${yariCap.toFixed(2)}px`,
        opacity: aktifId ? 1 : 0,
      }
    : { opacity: 0 };
  const cubukStili = lamba
    ? { transform: `translate3d(${Math.round(lamba.x + lamba.w / 2 - 16)}px,0,0)`, opacity: aktifId ? 1 : 0 }
    : { opacity: 0 };

  return (
    <nav
      data-testid="navbar"
      aria-label={t.gezinti}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        mobileOpen
          ? 'dark:bg-slate-950 bg-white shadow-sm'
          : scrolled
            ? 'dark:bg-slate-950/80 bg-white/80 backdrop-blur-xl border-b dark:border-white/10 border-slate-200/80 shadow-sm'
            : 'bg-transparent'
      }`}
    >
      {/* KAP: artık `max-w-6xl mx-auto` DEĞİL. Gerekçe ve rakamlar App.css
          `.wh-ust-kap` bloğunda. Kısaca: eski kap 1152px'te sabitleniyordu,
          1920px'te iki yanda 384'er piksel ölü alan bırakıyordu. */}
      <div className="wh-ust-kap">
        {/* BOYUT: eski 64 / 80 / 96px → yeni 60 / 72 / 86px (bir tık küçük). */}
        {/* Ara boşluk 1280px ALTINDA 8px'te bırakıldı: 1024px'te şeride kalan
            pay yalnızca ~11px ve her ek piksel CRM düğmesini kenara iter.
            1280px'ten sonra 71px pay var, orada 16px'e çıkıyor. */}
        <div className="flex items-center justify-between gap-2 xl:gap-4 h-[60px] sm:h-[72px] lg:h-[86px]">
          {/* --- Logo ---
              `truncate` KALDIRILDI, `min-w-0 shrink` KALDIRILDI.
              Ölçüm: `truncate` = overflow:hidden + text-overflow:ellipsis;
              `min-w-0 shrink` ise bu bağlantıyı satırdaki İLK fedakârlık
              adayı yapıyordu. Satır bir piksel bile taşsa kısalan hep marka
              adı oluyordu ("W…"). Deneyle kanıtlandı: yazı tipi %30 geniş
              olduğunda 320px'te scrollWidth=110 > clientWidth=108 → KIRPIK.
              Artık bağlantı `shrink-0`, yazı `whitespace-nowrap` ve boyut
              `clamp()` ile ölçekleniyor; kırpma matematiksel olarak imkânsız. */}
          <Link
            to="/"
            aria-label={t.anaSayfa}
            className="flex items-center gap-2 sm:gap-2.5 group logo-hover-glow shrink-0"
            data-testid="navbar-logo"
          >
            <img
              src="/assets/whowsell-logo.png"
              alt="WhowSell Logo"
              className="h-7 sm:h-10 lg:h-11 w-auto object-contain shrink-0 drop-shadow-[0_0_4px_rgba(6,182,212,0.2)] group-hover:drop-shadow-[0_0_14px_rgba(6,182,212,0.6)]"
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
            />
            <span
              data-testid="navbar-marka"
              className="wh-marka font-bold tracking-tight dark:text-white text-slate-800 group-hover:dark:text-cyan-400 group-hover:text-cyan-600"
            >
              WhowSell
            </span>
          </Link>

          {/* --- Masaüstü: tüp-ışıklı hap şerit --- */}
          <div
            ref={seritRef}
            data-testid="masaustu-serit"
            /* DİKKAT: `py-1 px-1` KALDIRILDI (bilinçli).
               Şeridin kendi dolgusu, lambanın ilk/son sekmede köşeye
               dayanmasını engelliyordu — ölçüldü: sol boşluk 5px
               (4px dolgu + 1px çerçeve). Boşluk artık sekmelerin KENDİ
               dolgusundan geliyor; şeridin dolgusu sıfır. */
            className="wh-serit hidden lg:flex items-center shrink-0
                       rounded-full border
                       dark:bg-white/[0.04] bg-slate-900/[0.03]
                       dark:border-white/10 border-slate-900/10
                       shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
          >
            {/* Lamba: iki eleman. Zemin scaleX ile esniyor, çubuk sabit
                genişlikte sadece kayıyor (bozulma yok). */}
            <span
              aria-hidden="true"
              data-testid="tup-lamba-zemin"
              className={`wh-lamba-zemin${ilkKare ? ' wh-lamba-zemin--ani' : ''}`}
              style={lambaStili}
            />
            <span
              aria-hidden="true"
              data-testid="tup-lamba"
              className={`wh-lamba${ilkKare ? ' wh-lamba--ani' : ''}`}
              style={cubukStili}
            >
              <i />
            </span>

            {navLinks.map((link, i) => {
              const aktif = aktifId === link.id;
              return (
                <a
                  key={link.id}
                  ref={el => { sekmeRef.current[link.id] = el; }}
                  data-testid={`masaustu-sekme-${i}`}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  aria-current={aktif ? 'true' : undefined}
                  title={aktif ? `${t.aktifBolum}: ${link.label}` : link.label}
                  /* Sekme ölçüleri (dolgu + yazı boyutu) artık Tailwind
                     kırılma noktalarıyla DEĞİL, App.css'teki `.wh-serit-sekme`
                     içinde `clamp()` ile sürekli ölçekleniyor. Sayısal
                     gerekçe orada. Burada yalnızca renk ve durum var. */
                  className={`wh-serit-sekme cursor-pointer rounded-full whitespace-nowrap font-semibold ${
                    aktif
                      ? 'dark:text-cyan-300 text-cyan-700'
                      : 'dark:text-white/60 text-slate-600 hover:dark:text-white hover:text-slate-900'
                  }`}
                >
                  {link.label}
                </a>
              );
            })}
          </div>

          {/* --- Sağ denetimler --- */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Dil değiştirici — küre değil BAYRAK.
                Düğme GEÇERLİ dilin bayrağını gösteriyor (tıklanınca gidilecek
                dilin değil). Eski davranış buydu: yanında yazan "TR"/"EN"
                metni de geçerli dildi. Bayrak metnin yerine değil, YANINA
                geldi — metin kalkarsa "bayrak hangi anlama geliyor" sorusu
                doğar ve tek başına bayrak, dil ile ülkeyi karıştırdığı için
                erişilebilirlik açısından da zayıf bir işarettir.
                İngilizce için Birleşik Krallık değil ABD bayrağı (Selçuk). */}
            <button
              data-testid="lang-toggle"
              data-dil={lang}
              onClick={toggleLang}
              className="wh-dokunma-dikey flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg sm:rounded-xl dark:bg-white/[0.06] bg-slate-100 dark:border-white/10 border-slate-200 border hover:dark:bg-white/[0.12] hover:bg-slate-200 transition-colors text-xs font-semibold dark:text-white/70 text-slate-600"
              aria-label={t.dilDegistir}
              title={t.dilDegistir}
            >
              <Bayrak dil={lang} en={20} />
              {lang.toUpperCase()}
            </button>

            {/* Tema değiştirici */}
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="wh-dokunma-dikey p-1.5 sm:p-2 rounded-lg sm:rounded-xl dark:bg-white/[0.06] bg-slate-100 dark:border-white/10 border-slate-200 border hover:dark:bg-white/[0.12] hover:bg-slate-200 transition-colors"
              aria-label={theme === 'dark' ? t.temaAcik : t.temaKoyu}
              title={theme === 'dark' ? t.temaAcik : t.temaKoyu}
            >
              {theme === 'dark'
                ? <Sun className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
                : <Moon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-600" />}
            </button>

            {/* CTA'lar — görünürlük SARMALAYICIDA.
                `.wh-cta` ile `hidden` aynı elemanda olursa App.css'in yükleme
                sırası yüzünden `hidden` eziliyor (dosya başındaki nota bakın).
                Düğmelerin kendi sınıfları (`wh-cta wh-cta--kucuk`) ve
                clamp() boyutlandırması aynen duruyor. */}
            <div className="hidden sm:flex items-center gap-1.5 sm:gap-2 shrink-0">
              <button
                data-testid="demo-dugmesi"
                onClick={() => setShowDemoPopup(true)}
                className="wh-cta wh-cta--kucuk transition-all duration-300 active:scale-[0.97] hover:scale-[1.02] cursor-pointer"
                style={{
                  whiteSpace: 'nowrap',
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(6,182,212,0.08))',
                  color: '#a78bfa',
                  border: '1.5px solid rgba(139,92,246,0.3)',
                  boxShadow: '0 0 16px rgba(139,92,246,0.1), 0 2px 8px rgba(0,0,0,0.08)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139,92,246,0.22), rgba(6,182,212,0.15))';
                  e.currentTarget.style.boxShadow = '0 0 24px rgba(139,92,246,0.2), 0 4px 12px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(6,182,212,0.08))';
                  e.currentTarget.style.boxShadow = '0 0 16px rgba(139,92,246,0.1), 0 2px 8px rgba(0,0,0,0.08)';
                }}
              >
                {t.demoButton}
              </button>

              <a
                data-testid="crm-dugmesi"
                href="https://crm.whowsell.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="wh-cta wh-cta--kucuk bg-gradient-to-b from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-950 transition-all shadow-[0_0_0_1px_rgba(6,182,212,0.35),0_4px_16px_rgba(6,182,212,0.25)] active:scale-[0.97]"
                style={{ whiteSpace: 'nowrap' }}
              >
                {t.crmButton}
              </a>
            </div>

            {/* Hamburger — dönüşen SVG (MenuToggleIcon). */}
            <button
              ref={hamburgerRef}
              data-testid="mobil-menu-dugmesi"
              type="button"
              className="lg:hidden flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 shrink-0 rounded-lg sm:rounded-xl dark:bg-white/[0.06] bg-slate-100 border dark:border-white/10 border-slate-200 dark:text-white/80 text-slate-700 hover:dark:bg-white/[0.12] hover:bg-slate-200 transition-colors"
              onClick={() => setMobileOpen(o => !o)}
              aria-expanded={mobileOpen}
              aria-controls="wh-mobil-menu"
              aria-label={mobileOpen ? t.menuKapat : t.menuAc}
              title={mobileOpen ? t.menuKapat : t.menuAc}
            >
              <MenuToggleIcon acik={mobileOpen} sure={500} boyut={20} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= MOBİL MENÜ ================= */}
      {mobileOpen && (
        <div
          id="wh-mobil-menu"
          data-testid="mobil-menu"
          ref={mobilPanelRef}
          className="lg:hidden border-b"
          style={{
            background: theme === 'dark'
              ? 'linear-gradient(180deg, #020617 0%, #0f172a 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)',
            borderColor: theme === 'dark' ? 'rgba(6,182,212,0.12)' : 'rgba(203,213,225,0.6)',
            animation: 'mobileMenuSlide 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            marginTop: 0,
            paddingTop: 0,
          }}
        >
          <div style={{ padding: '14px 18px 18px' }}>
            {/* Bölüm bağlantıları */}
            <nav aria-label={t.gezinti} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {navLinks.map((link, idx) => {
                const Icon = link.icon;
                const aktif = aktifId === link.id;
                return (
                  <a
                    key={link.id}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    aria-current={aktif ? 'true' : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '12px 14px',
                      borderRadius: 14,
                      textDecoration: 'none',
                      transition: 'background 0.2s ease',
                      animation: `mobileItemFade 0.3s cubic-bezier(0.16,1,0.3,1) ${idx * 0.04}s both`,
                      /* Aktif bölüm mobilde de gerçek: hafif bir zemin + sol
                         kenarda tüp ışığın rengi. */
                      background: aktif
                        ? (theme === 'dark' ? 'rgba(34,211,238,0.08)' : 'rgba(6,182,212,0.07)')
                        : 'transparent',
                      boxShadow: aktif
                        ? `inset 3px 0 0 ${theme === 'dark' ? '#22d3ee' : '#0891b2'}`
                        : 'none',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (aktif) return;
                      e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
                    }}
                    onMouseLeave={(e) => {
                      if (aktif) return;
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        background: theme === 'dark'
                          ? 'linear-gradient(135deg, rgba(6,182,212,0.1), rgba(139,92,246,0.08))'
                          : 'linear-gradient(135deg, rgba(6,182,212,0.08), rgba(139,92,246,0.05))',
                        border: `1px solid ${theme === 'dark' ? 'rgba(6,182,212,0.15)' : 'rgba(6,182,212,0.12)'}`,
                      }}
                    >
                      <Icon
                        size={16}
                        aria-hidden="true"
                        style={{ color: theme === 'dark' ? 'rgba(34,211,238,0.75)' : 'rgba(6,182,212,0.85)' }}
                      />
                    </span>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: aktif ? 600 : 500,
                        whiteSpace: 'nowrap',
                        color: aktif
                          ? (theme === 'dark' ? '#67e8f9' : '#0e7490')
                          : (theme === 'dark' ? 'rgba(255,255,255,0.75)' : 'rgba(51,65,85,0.9)'),
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {link.label}
                    </span>
                  </a>
                );
              })}
            </nav>

            {/* Renk geçişli ayraç */}
            <div
              aria-hidden="true"
              style={{
                height: 1,
                margin: '13px 0',
                background: theme === 'dark'
                  ? 'linear-gradient(90deg, transparent, rgba(6,182,212,0.2), rgba(139,92,246,0.2), transparent)'
                  : 'linear-gradient(90deg, transparent, rgba(6,182,212,0.15), rgba(139,92,246,0.15), transparent)',
              }}
            />

            {/* CTA düğmeleri */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, animation: 'mobileItemFade 0.4s cubic-bezier(0.16,1,0.3,1) 0.25s both' }}>
              <button
                data-testid="mobil-demo-dugmesi"
                onClick={() => { setMobileOpen(false); setShowDemoPopup(true); }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: '13px 18px',
                  borderRadius: 14,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  letterSpacing: '-0.01em',
                  color: '#a78bfa',
                  background: theme === 'dark'
                    ? 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(6,182,212,0.08))'
                    : 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(6,182,212,0.05))',
                  border: `1.5px solid ${theme === 'dark' ? 'rgba(139,92,246,0.25)' : 'rgba(139,92,246,0.2)'}`,
                  boxShadow: theme === 'dark'
                    ? '0 0 20px rgba(139,92,246,0.08), 0 2px 8px rgba(0,0,0,0.15)'
                    : '0 0 12px rgba(139,92,246,0.06), 0 2px 6px rgba(0,0,0,0.04)',
                  transition: 'background 0.25s ease, box-shadow 0.25s ease',
                }}
              >
                <Rocket size={16} aria-hidden="true" />
                {t.demoButton}
              </button>

              <a
                data-testid="mobil-crm-dugmesi"
                href="https://crm.whowsell.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: '13px 18px',
                  borderRadius: 14,
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  letterSpacing: '-0.01em',
                  color: '#0f172a',
                  background: 'linear-gradient(135deg, #06b6d4, #22d3ee)',
                  border: '1px solid rgba(6,182,212,0.4)',
                  boxShadow: '0 0 0 1px rgba(6,182,212,0.3), 0 4px 16px rgba(6,182,212,0.2), 0 8px 24px rgba(0,0,0,0.1)',
                  transition: 'box-shadow 0.25s ease',
                }}
              >
                <ExternalLink size={16} aria-hidden="true" />
                {t.crmButton}
              </a>
            </div>
          </div>
        </div>
      )}

      <EnterpriseDemoPopup isOpen={showDemoPopup} onClose={() => setShowDemoPopup(false)} />
    </nav>
  );
}
